import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  const radioRef = React.useRef<HTMLButtonElement>(null);
  const combinedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      radioRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const handleFocus = React.useCallback((_: React.FocusEvent<HTMLButtonElement>) => {
    if (radioRef.current) {
      const isInvalid = radioRef.current.getAttribute('aria-invalid') === 'true';
      const computedStyle = window.getComputedStyle(radioRef.current);
      // border-radius를 명시적으로 유지 (원형)
      const borderRadius = computedStyle.borderRadius || '9999px';
      radioRef.current.style.setProperty('border-radius', borderRadius, 'important');

      // border-color만 변경
      if (!isInvalid) {
        radioRef.current.style.setProperty('border-color', '#000000', 'important');
      } else {
        radioRef.current.style.setProperty('border-color', '#ef4444', 'important');
      }

      // Ring/outline 제거
      radioRef.current.style.setProperty('outline', 'none', 'important');
      radioRef.current.style.setProperty('outline-width', '0', 'important');
      radioRef.current.style.setProperty('outline-style', 'none', 'important');
      radioRef.current.style.setProperty('outline-offset', '0', 'important');
      radioRef.current.style.setProperty('box-shadow', 'none', 'important');
      radioRef.current.style.setProperty('--tw-ring-offset-shadow', 'none', 'important');
      radioRef.current.style.setProperty('--tw-ring-shadow', 'none', 'important');
    }
  }, []);

  const handleBlur = React.useCallback((_: React.FocusEvent<HTMLButtonElement>) => {
    if (radioRef.current) {
      const isInvalid = radioRef.current.getAttribute('aria-invalid') === 'true';
      const computedStyle = window.getComputedStyle(radioRef.current);
      // border-radius를 명시적으로 유지 (원형)
      const borderRadius = computedStyle.borderRadius || '9999px';
      radioRef.current.style.setProperty('border-radius', borderRadius, 'important');

      if (isInvalid) {
        radioRef.current.style.setProperty('border-color', '#ef4444', 'important');
      } else {
        radioRef.current.style.removeProperty('border-color');
      }

      // Ring/outline 제거 유지
      radioRef.current.style.setProperty('outline', 'none', 'important');
      radioRef.current.style.setProperty('box-shadow', 'none', 'important');
    }
  }, []);

  return (
    <RadioGroupPrimitive.Item
      ref={combinedRef}
      className={cn(
        'aspect-square h-4 w-4 rounded-full border border-[#d9dde0] text-primary',
        // Ring/outline 완전히 제거
        'outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
        // Error 상태
        'aria-invalid:border-red-500',
        // Disabled 상태
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Checked 상태 - border도 primary로 변경
        'data-[state=checked]:border-primary',
        // Transition
        'transition-colors',
        className,
      )}
      style={{
        outline: 'none',
        boxShadow: 'none',
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-3.5 w-3.5 fill-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
